import React, { useState, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import utc from 'dayjs/plugin/utc';
import dayjs from 'dayjs';
import { Form, Col, Select, Input, DatePicker } from 'antd';
import { LabelTooltipType } from 'antd/lib/form/FormItemLabel';

import { filterTypes } from '../../constants/filterType';

import { renderNoDataOrSpinner } from '../../utils';

import './styles.less';

const { RangePicker } = DatePicker;

dayjs.extend(utc);

interface FilterProps {
  filterType: number;
  name: string;
  label: string;
  span?: number;
  data?: Array<Record<string, any>>;
  dataLoading?: boolean;
  dataSearchAction?: (searchString?: string) => any;
  mode?: 'multiple' | 'tags' | 'single' | undefined;
  keyPropertyName?: string;
  valuePropertyName?: string;
  titlePropertyName?: string | ((data: any) => string);
  textPropertyName?: string | ((data: any) => string);
  filterByChildData?: boolean;
  placeholder?: [string, string] | string;
  wrapperCol?: object;
  maxTagCount?: number;
  subscribeDataChangingAndFetchNewData?: any;
  rules?: Array<any>;
  fetchImmediately?: boolean;
  onChange?: (value: any) => void;
  disabled?: boolean;
  inputType?: string;
  tooltip?: LabelTooltipType;
}

export const Filter = ({
  filterType,
  name,
  label,
  span,
  data,
  dataLoading,
  dataSearchAction,
  mode,
  keyPropertyName,
  valuePropertyName,
  titlePropertyName,
  textPropertyName,
  filterByChildData,
  placeholder,
  wrapperCol,
  maxTagCount,
  subscribeDataChangingAndFetchNewData,
  rules,
  fetchImmediately,
  onChange,
  disabled,
  inputType,
  tooltip,
}: FilterProps) => {
  const [fieldTimeout, setFieldTimeout] = useState<NodeJS.Timeout | undefined>();
  const [searchFieldValue, setSearchFieldValue] = useState<string>('');
  const [canFetchData, setCanFetchData] = useState<boolean>(false);
  const [subscriptionOnDropdownOpen, setSubscriptionOnDropdownOpen] = useState<(() => void) | undefined>();

  const didMount = useRef(false);

  const dispatch = useDispatch();

  useEffect(() => {
    if (!didMount.current) {
      onMount();
      didMount.current = true;
    }
    return onUnmount;
  }, []);

  useEffect(() => fetchData(), [subscribeDataChangingAndFetchNewData, canFetchData]);

  const onMount = () => {
    if (typeof subscriptionOnDropdownOpen !== 'function') {
      setSubscriptionOnDropdownOpen(() => onDropdownVisibleChange);
    }

    fetchData();
  };

  const onUnmount = () => {
    if (fieldTimeout) {
      clearTimeout(fieldTimeout);
      setFieldTimeout(undefined);
    }

    if (typeof subscriptionOnDropdownOpen === 'function') {
      setSubscriptionOnDropdownOpen(undefined);
    }

    if (canFetchData) {
      setCanFetchData(false);
    }
  };

  const fetchData = () => {
    if (typeof dataSearchAction === 'function' && (canFetchData || fetchImmediately)) {
      dispatch(dataSearchAction());
    }
  };

  const handleSearch = (searchString: string) => {
    if (typeof dataSearchAction === 'function') {
      if (fieldTimeout) {
        clearTimeout(fieldTimeout);
        setFieldTimeout(undefined);
      }

      setSearchFieldValue(searchString);

      const timeout = setTimeout(() => dispatch(dataSearchAction(searchString)), 500);
      setFieldTimeout(timeout);
    }
  };

  const getMaxTagPlaceholder = (omittedValues: any[]) => {
    if (omittedValues && omittedValues.length > 0) {
      return `+${omittedValues.length} Items`;
    }
  };

  const onDropdownVisibleChange = (open: boolean) => {
    if (!canFetchData) {
      setCanFetchData(true);
    }
  };

  const createDropdownYesNo = () => {
    return (
      <Form.Item name={[name]} label={label} className="filter-form-item" wrapperCol={wrapperCol} rules={rules} tooltip={tooltip}>
        <Select
          allowClear
          filterOption={false}
          placeholder={placeholder ?? `Select ${label}`}
          onChange={onChange}
          disabled={disabled}
        >
          <Select.Option key="1" value={1} title="Empty">
            Empty
          </Select.Option>
          <Select.Option key="2" value={2} title="No">
            No
          </Select.Option>
          <Select.Option key="3" value={3} title="Yes">
            Yes
          </Select.Option>
        </Select>
      </Form.Item>
    );
  };

  const createInput = () => {
    return (
      <Form.Item name={[name]} label={label} className="filter-form-item" wrapperCol={wrapperCol} rules={rules} tooltip={tooltip}>
        <Input type={inputType ?? 'text'} placeholder={placeholder as string ?? `Enter ${label}`} onChange={onChange} disabled={disabled} />
      </Form.Item>
    );
  };

  const createDropdownWithLocalData = () => {
    const key = keyPropertyName ? keyPropertyName : 'key';
    const value = valuePropertyName ? valuePropertyName : key;
    const text = textPropertyName ? textPropertyName : 'value';
    const title = titlePropertyName ? titlePropertyName : text;

    return (
      <Form.Item name={[name]} label={label} className="filter-form-item" wrapperCol={wrapperCol} rules={rules} tooltip={tooltip}>
        <Select
          allowClear
          showSearch
          mode={mode ? mode as any : 'multiple'}
          placeholder={placeholder ?? `Select ${label}`}
          optionFilterProp="children"
          filterOption={(input, option) => (option?.children as unknown as string).toLowerCase().indexOf(input.toLowerCase()) >= 0}
          maxTagCount={maxTagCount ?? 4}
          maxTagPlaceholder={getMaxTagPlaceholder}
          onChange={onChange}
          disabled={disabled}
          notFoundContent={renderNoDataOrSpinner(false)}
          loading={dataLoading}
        >
          {data && data.length
            ? data.map((d) => (
              <Select.Option key={d[key]} value={d[value]} title={typeof title === 'function' ? title(d) : d[title]}>
                {typeof text === 'function' ? text(d) : d[text]}
              </Select.Option>
            ))
            : null}
        </Select>
      </Form.Item>
    );
  };

  const createDropdownWithServerData = () => {
    const key = keyPropertyName ? keyPropertyName : 'key';
    const value = valuePropertyName ? valuePropertyName : key;
    const text = textPropertyName ? textPropertyName : 'value';
    const title = titlePropertyName ? titlePropertyName : text;

    const filtering = filterByChildData
      ? {
        optionFilterProp: 'children',
        filterOption: (input: string, option: any) => (option?.children as string).toLowerCase().indexOf(input.toLowerCase()) >= 0,
      }
      : {
        onSearch: handleSearch,
        filterOption: false,
        searchValue: searchFieldValue,
      };

    return (
      <Form.Item name={[name]} label={label} className="filter-form-item" wrapperCol={wrapperCol} rules={rules} tooltip={tooltip}>
        <Select
          {...filtering}
          allowClear
          autoClearSearchValue={false}
          showSearch
          mode={mode ? mode as any : 'multiple'}
          loading={dataLoading}
          notFoundContent={renderNoDataOrSpinner(dataLoading)}
          maxTagCount={maxTagCount ?? 4}
          maxTagPlaceholder={getMaxTagPlaceholder}
          placeholder={searchFieldValue ? searchFieldValue : (placeholder ?? `Select ${label}`)}
          onDropdownVisibleChange={subscriptionOnDropdownOpen}
          onChange={onChange}
          disabled={disabled}
        >
          {data && data.length
            ? data.map((d) => (
              <Select.Option key={d[key]} value={d[value]} title={typeof title === 'function' ? title(d) : d[title]}>
                {typeof text === 'function' ? text(d) : d[text]}
              </Select.Option>
            ))
            : null}
        </Select>
      </Form.Item>
    );
  };

  const createRangePicker = () => {
    return (
      <Form.Item name={[name]} label={label} className="filter-form-item" wrapperCol={wrapperCol} rules={rules} tooltip={tooltip}>
        <RangePicker
          allowEmpty={[true, true]}
          style={{ width: '100%' }}
          allowClear
          format={(date) => date.utc().format('MM/DD/YYYY')}
          placeholder={placeholder as [string, string] ?? ['Date From:', 'Date To:']}
          presets={[
            { label: 'Today', value: [dayjs().utc().startOf('day'), dayjs().utc().endOf('day')] },
            { label: 'This Month', value: [dayjs().utc().startOf('month'), dayjs().utc().endOf('month')] },
            { label: 'Last Month', value: [dayjs().subtract(1, 'month').utc().startOf('month'), dayjs().subtract(1, 'month').utc().endOf('month')] },
          ]}
          onChange={onChange}
          disabled={disabled}
        />
      </Form.Item>
    );
  };

  let filter;

  switch (filterType) {
    case filterTypes.dropdownYesNo:
      filter = createDropdownYesNo();
      break;
    case filterTypes.inputText:
      filter = createInput();
      break;
    case filterTypes.dropdownWithLocalData:
      filter = createDropdownWithLocalData();
      break;
    case filterTypes.dropdownWithServerData:
      filter = createDropdownWithServerData();
      break;
    case filterTypes.rangePicker:
      filter = createRangePicker();
      break;
    default:
      filter = null; // Ensure a valid JSX return value
  }

  if (span && filter) {
    filter = <Col span={span}>{filter}</Col>;
  }

  return filter ?? null; // Ensure a valid JSX return value
};

export default Filter;
